import type { ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import { ArrowLeft, Clock3, Lock, Paperclip } from 'lucide-react';
import { TINT_CLASS, type CatalogForm } from '@generated/lib/formsCatalog';
import { cn } from '@lib/utils';

interface Props {
  form: CatalogForm;
  index: number;
  /** Secondary action rendered OUTSIDE the card's link, so it stays independently clickable. */
  footerAction?: ReactNode;
}

const PAD = 'p-5 sm:p-6';

function CardBody({ form, index }: { form: CatalogForm; index: number }) {
  return (
    <>
      <div className="flex items-start justify-between gap-4">
        <span className="text-[11px] font-medium tracking-[0.16em] text-muted-foreground">
          {String(index + 1).padStart(2, '0')}
        </span>
        {form.status === 'soon' ? (
          <span className="inline-flex items-center gap-1.5 rounded-none border border-[hsl(var(--rule))] bg-[hsl(var(--surface-1))] px-2.5 py-1 text-[10px] font-medium tracking-[0.08em] text-muted-foreground">
            <Lock className="size-3" aria-hidden="true" />
            בקרוב
          </span>
        ) : (
          <span className="inline-flex items-center gap-1.5 rounded-none border border-primary bg-[hsl(var(--tint-sky))] px-2.5 py-1 text-[10px] font-medium tracking-[0.08em] text-primary">
            פתוח להגשה
          </span>
        )}
      </div>

      <h3 className="mt-5 text-xl font-medium leading-tight text-foreground sm:text-2xl">{form.title}</h3>
      <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{form.summary}</p>

      <ul className="mt-5 grid gap-1.5 border-t border-[hsl(var(--rule-soft))] pt-4">
        <li className="flex items-center gap-2 text-xs text-muted-foreground">
          <Clock3 className="size-3.5 shrink-0" aria-hidden="true" />
          זמן מילוי משוער: כ-{form.minutes} דקות
        </li>
        {form.requirements.map((requirement) => (
          <li key={requirement} className="flex items-center gap-2 text-xs text-muted-foreground">
            <Paperclip className="size-3.5 shrink-0" aria-hidden="true" />
            {requirement}
          </li>
        ))}
      </ul>

      <p
        className={cn(
          'mt-5 inline-flex items-center gap-2 text-[13px] font-medium',
          form.status === 'soon' ? 'text-muted-foreground' : 'text-foreground',
        )}
      >
        {form.status === 'soon' ? 'הטופס ייפתח בהמשך' : 'מילוי הטופס'}
        {form.status === 'available' ? <ArrowLeft className="size-4" aria-hidden="true" /> : null}
      </p>
    </>
  );
}

export function FormCard({ form, index, footerAction }: Props) {
  const shell = cn('flex h-full flex-col rounded-lg border border-[hsl(var(--rule))]', TINT_CLASS[form.tint]);
  const target =
    form.id === 'teaching-assignment'
      ? '/forms/teaching-assignment'
      : form.id === 'postdoc-bursary'
        ? '/forms/postdoc-bursary'
        : form.id === 'post-bidding'
          ? '/forms/post-bidding'
          : '/forms/military-declaration';
  const hover =
    'transition-colors duration-150 hover:bg-[hsl(var(--surface-3))] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[hsl(var(--ring))]';

  if (form.status === 'soon') {
    return (
      <div aria-disabled="true" className={cn(shell, PAD, 'opacity-70')}>
        <CardBody form={form} index={index} />
      </div>
    );
  }

  // A nested button inside a Link is not clickable, so the card splits into a linked area plus a
  // separate action row whenever a secondary action is supplied.
  if (footerAction) {
    return (
      <div className={shell}>
        <Link to={target} className={cn('flex flex-1 flex-col rounded-t-lg', PAD, hover)}>
          <CardBody form={form} index={index} />
        </Link>
        <div className="border-t border-[hsl(var(--rule-soft))] px-5 pb-5 pt-4 sm:px-6">{footerAction}</div>
      </div>
    );
  }

  return (
    <Link to={target} className={cn(shell, PAD, hover)}>
      <CardBody form={form} index={index} />
    </Link>
  );
}
